import { useState } from 'react';
import BoardsTour from '@/components/tours/BoardsTour';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { usePermissions } from '@/contexts/WorkspaceContext';
import {
    Kanban,
    Plus,
    Loader2,
    ArrowRight,
    X,
    Edit3,
    Trash2,
    MoreVertical,
    Lock,
    Target,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { usePageTitle } from '@/hooks/usePageTitle';

export default function BoardsPage() {
    const { t } = useTranslation();
    usePageTitle('nav.boards');
    const { workspace } = useAuthStore();
    const { canEdit } = usePermissions();
    const queryClient = useQueryClient();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingBoard, setEditingBoard] = useState<any>(null);

    const { data, isLoading } = useQuery({
        queryKey: ['boards', workspace?.id],
        queryFn: () => api.getBoards(workspace!.id),
        enabled: !!workspace?.id,
    });

    const deleteMutation = useMutation({
        mutationFn: (boardId: string) => api.deleteBoard(boardId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['boards'] });
        },
    });

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        );
    }

    const boards = data?.data || [];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">{t('boards.title')}</h1>
                    <p className="text-neutral-600 dark:text-neutral-400">
                        {t('boards.description') || 'Manage your Kanban boards'}
                    </p>
                </div>
                {canEdit ? (
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="btn-primary"
                        data-tour="add-board-btn"
                    >
                        <Plus className="w-4 h-4" />
                        {t('boards.addBoard')}
                    </button>
                ) : (
                    <div className="flex items-center gap-2 text-sm text-neutral-500 bg-neutral-100 dark:bg-neutral-700 px-3 py-2 rounded-lg">
                        <Lock className="w-4 h-4" />
                        {t('permissions.viewOnly', 'Chế độ chỉ xem')}
                    </div>
                )}
            </div>

            {/* Boards Grid */}
            {boards.length === 0 ? (
                <div className="card p-12 text-center">
                    <Kanban className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">{t('boards.noBoards')}</h3>
                    <p className="text-neutral-600 dark:text-neutral-400 mb-6 max-w-md mx-auto">
                        {t('boards.noBoards')}
                    </p>
                    {canEdit && (
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="btn-primary"
                        >
                            <Plus className="w-4 h-4" />
                            {t('boards.addBoard')}
                        </button>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {boards.map((board) => (
                        <BoardCard
                            key={board.id}
                            board={board}
                            canEdit={canEdit}
                            onEdit={() => setEditingBoard(board)}
                            onDelete={() => {
                                if (confirm(`Delete "${board.name}"? This will delete all tasks in this board.`)) {
                                    deleteMutation.mutate(board.id);
                                }
                            }}
                        />
                    ))}

                    {/* Add New Board Card - only for editors */}
                    {canEdit && (
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="card p-6 border-2 border-dashed border-neutral-300 dark:border-neutral-600 hover:border-primary-400 hover:bg-primary-50/50 dark:hover:bg-primary-600/10 transition-colors flex flex-col items-center justify-center min-h-[160px] group"
                        >
                            <div className="w-12 h-12 bg-neutral-100 dark:bg-neutral-700 rounded-xl flex items-center justify-center mb-3 group-hover:bg-primary-100 dark:group-hover:bg-primary-600/20 transition-colors">
                                <Plus className="w-6 h-6 text-neutral-400 group-hover:text-primary-600" />
                            </div>
                            <span className="text-neutral-500 group-hover:text-primary-600 font-medium">
                                Add Board
                            </span>
                        </button>
                    )}
                </div>
            )}

            {/* Create Modal */}
            {showCreateModal && (
                <BoardModal
                    workspaceId={workspace?.id || ''}
                    onClose={() => setShowCreateModal(false)}
                />
            )}

            {/* Edit Modal */}
            {editingBoard && (
                <BoardModal
                    workspaceId={workspace?.id || ''}
                    board={editingBoard}
                    onClose={() => setEditingBoard(null)}
                />
            )}

            {/* Onboarding Tour */}
            <BoardsTour />
        </div>
    );
}

function BoardCard({
    board,
    canEdit,
    onEdit,
    onDelete
}: {
    board: any;
    canEdit: boolean;
    onEdit: () => void;
    onDelete: () => void;
}) {
    const [showMenu, setShowMenu] = useState(false);
    const navigate = useNavigate();

    const colorClasses: Record<string, string> = {
        blue: 'bg-primary-500',
        green: 'bg-success-500',
        yellow: 'bg-warning-500',
        red: 'bg-danger-500',
        purple: 'bg-secondary-500',
        default: 'bg-gradient-to-br from-primary-500 to-secondary-500',
    };

    return (
        <div className="card overflow-hidden group">
            {/* Color header */}
            <div
                className={clsx(
                    'h-20 relative',
                    colorClasses[board.color] || colorClasses.default
                )}
            >
                {/* Menu button - only for editors */}
                {canEdit && (
                    <div className="absolute top-2 right-2">
                        <button
                            onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
                            className="p-1.5 bg-white/20 hover:bg-white/40 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                            <MoreVertical className="w-4 h-4 text-white" />
                        </button>

                        {showMenu && (
                            <div className="absolute right-0 top-full mt-1 bg-white dark:bg-neutral-800 rounded-lg shadow-lg border border-neutral-200 dark:border-neutral-700 py-1 w-32 z-10">
                                <button
                                    onClick={(e) => { e.stopPropagation(); setShowMenu(false); onEdit(); }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700"
                                >
                                    <Edit3 className="w-4 h-4" />
                                    Edit
                                </button>
                                <button
                                    onClick={(e) => { e.stopPropagation(); setShowMenu(false); onDelete(); }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-600/10"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Delete
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Content */}
            <div
                className="p-4 cursor-pointer"
                onClick={() => navigate(`/boards/${board.id}`)}
            >
                <h3 className="font-semibold mb-1 truncate">{board.name}</h3>
                {board.description && (
                    <p className="text-sm text-neutral-500 line-clamp-2 mb-3">{board.description}</p>
                )}
                <div className="flex items-center justify-between text-sm">
                    <span className="text-neutral-500">{board.task_count || 0} tasks</span>
                    <ArrowRight className="w-4 h-4 text-primary-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
            </div>
        </div>
    );
}

function BoardModal({
    workspaceId,
    board,
    onClose,
}: {
    workspaceId: string;
    board?: any;
    onClose: () => void;
}) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const isEdit = !!board;

    const [formData, setFormData] = useState({
        name: board?.name || '',
        description: board?.description || '',
        template: 'default',
        color: board?.color || 'blue',
        linked_kr_id: board?.linked_kr_id || '',
    });

    // Fetch available KRs from objectives
    const { data: okrData } = useQuery({
        queryKey: ['objectives', workspaceId],
        queryFn: () => api.getObjectives(workspaceId),
        enabled: !!workspaceId,
    });

    // Get all available KRs from objectives
    const allKRs: any[] = [];
    okrData?.data?.forEach((obj: any) => {
        obj.key_results?.forEach((kr: any) => {
            allKRs.push({
                ...kr,
                objective_title: obj.title,
            });
        });
    });

    const colors = [
        { value: 'blue', label: 'Blue', class: 'bg-primary-500' },
        { value: 'green', label: 'Green', class: 'bg-success-500' },
        { value: 'yellow', label: 'Yellow', class: 'bg-warning-500' },
        { value: 'red', label: 'Red', class: 'bg-danger-500' },
        { value: 'purple', label: 'Purple', class: 'bg-secondary-500' },
    ];

    const createMutation = useMutation({
        mutationFn: () => api.createBoard({
            workspace_id: workspaceId,
            name: formData.name,
            description: formData.description || undefined,
            template: formData.template,
            linked_kr_id: formData.linked_kr_id || undefined,
        }),
        onSuccess: (data: any) => {
            queryClient.invalidateQueries({ queryKey: ['boards'] });
            onClose();
            if (data?.data?.id) {
                navigate(`/boards/${data.data.id}`);
            }
        },
    });

    const updateMutation = useMutation({
        mutationFn: () => api.updateBoard(board.id, {
            name: formData.name,
            description: formData.description || undefined,
            color: formData.color,
            linked_kr_id: formData.linked_kr_id || undefined,
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['boards'] });
            onClose();
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (isEdit) {
            updateMutation.mutate();
        } else {
            createMutation.mutate();
        }
    };

    const isPending = createMutation.isPending || updateMutation.isPending;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl w-full max-w-md animate-scale-in">
                <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700">
                    <h2 className="text-lg font-semibold">
                        {isEdit ? t('boards.editBoard', 'Sửa Board') : t('boards.createBoard', 'Tạo Board')}
                    </h2>
                    <button onClick={onClose} className="p-1 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-4 space-y-4">
                    <div>
                        <label className="label">{t('common.name', 'Tên')}</label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="input"
                            placeholder={t('boards.namePlaceholder', 'VD: Sprint 1')}
                            required
                            autoFocus
                        />
                    </div>

                    <div>
                        <label className="label">{t('boards.descriptionOptional', 'Mô tả (tùy chọn)')}</label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            className="input min-h-[60px]"
                            placeholder={t('boards.descPlaceholder', 'Board này dùng để làm gì?')}
                        />
                    </div>

                    {/* KR Linking - NEW FEATURE */}
                    <div>
                        <label className="label flex items-center gap-2">
                            <Target className="w-4 h-4 text-primary-600" />
                            {t('boards.linkedKR', 'Liên kết với Key Result')}
                            <span className="text-xs text-neutral-400">({t('common.optional', 'tùy chọn')})</span>
                        </label>
                        <select
                            value={formData.linked_kr_id}
                            onChange={(e) => setFormData({ ...formData, linked_kr_id: e.target.value })}
                            className="input"
                        >
                            <option value="">{t('boards.noKRLinked', 'Không liên kết')}</option>
                            {allKRs.map((kr) => (
                                <option key={kr.id} value={kr.id}>
                                    {kr.title} ({kr.objective_title})
                                </option>
                            ))}
                        </select>
                        {formData.linked_kr_id && (
                            <p className="text-xs text-neutral-500 mt-1">
                                💡 {t('boards.krLinkInfo', 'Tất cả task trong board này sẽ được tự động liên kết với KR đã chọn.')}
                            </p>
                        )}
                    </div>

                    {/* Color picker */}
                    <div>
                        <label className="label">{t('common.color', 'Màu sắc')}</label>
                        <div className="flex gap-2">
                            {colors.map((color) => (
                                <button
                                    key={color.value}
                                    type="button"
                                    onClick={() => setFormData({ ...formData, color: color.value })}
                                    className={clsx(
                                        'w-8 h-8 rounded-lg',
                                        color.class,
                                        formData.color === color.value && 'ring-2 ring-offset-2 ring-primary-500'
                                    )}
                                    title={color.label}
                                />
                            ))}
                        </div>
                    </div>

                    {!isEdit && (
                        <div>
                            <label className="label">{t('common.template', 'Mẫu')}</label>
                            <select
                                value={formData.template}
                                onChange={(e) => setFormData({ ...formData, template: e.target.value })}
                                className="input"
                            >
                                <option value="default">{t('boards.templates.default', 'Mặc định (Cần làm, Đang làm, Hoàn thành)')}</option>
                                <option value="blank">{t('boards.templates.blank', 'Bảng trống')}</option>
                                <option value="scrum">{t('boards.templates.scrum', 'Scrum (Backlog, Sprint, Đang làm, Review, Done)')}</option>
                            </select>
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="btn-secondary">
                            {t('common.cancel', 'Hủy')}
                        </button>
                        <button type="submit" disabled={isPending} className="btn-primary">
                            {isPending ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <>
                                    {isEdit ? <Edit3 className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                                    {isEdit ? t('common.save', 'Lưu') : t('common.create', 'Tạo')}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
